import { useState, useEffect, useRef, useContext } from 'react';
import { FaComments, FaTimes, FaPaperPlane, FaWhatsapp, FaSync, FaCalendarAlt, FaClipboardList, FaUserTie, FaStore, FaShoppingCart, FaChartLine, FaUserCircle } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import HttpInterceptor from '../lib/HttpInterceptor';
import UserContext from './UserContext';
import Logout from './Logout';

// Using a reliable avatar service for student chatbot - different female avatar from Priya and Ananya
const radhikaAvatar = '/assets/ai-agents/vedika_numerology.png';

/**
 * Student-Specific ChatBot Component
 * Provides assistance for students/members with learning-focused FAQs and functionality
 */

// Student-specific FAQ data
const studentFaqData = [
  {
    keywords: ['enroll', 'enrollment', 'join program', 'sign up program', 'buy program', 'purchase', 'enroll in'],
    question: 'How do I enroll in a program?',
    answer: `Enrolling in a program is easy! Here's how:

1. **Browse Marketplace** - Go to "Marketplace" to see all available programs
2. **Choose a Program** - Browse by service type (Yoga, Dietitian, Gym, etc.) or search for specific trainers
3. **View Details** - Click on a program to see pricing, schedule, and trainer information
4. **Enroll** - Click "Enroll" or "Buy Now" to join the program
5. **Complete Payment** - Follow the payment process to secure your spot

Once enrolled, you'll see the program in "My Plans" and can start attending classes! Need help finding a specific program?`,
    quickActions: [
      { text: 'Browse Marketplace', link: '/student/market' },
      { text: 'View My Plans', link: '/student/plans' }
    ]
  },
  {
    keywords: ['class', 'classes', 'session', 'sessions', 'my classes', 'upcoming', 'schedule', 'attendance'],
    question: 'How do I view my classes?',
    answer: `Viewing your classes is simple! Here's what you can do:

📅 **Access Classes** - Go to "My Plans" and click "Access Classes" to see all your scheduled classes
📊 **Upcoming Classes** - View classes scheduled for today, this week, or this month
✅ **Mark Attendance** - Check in when you attend a class
📈 **History** - See your past classes and attendance records
🔔 **Reminders** - Get notified about upcoming classes

You can also see your upcoming classes right on your dashboard. Want to check your schedule now?`,
    quickActions: [
      { text: 'My Plans', link: '/student/plans' },
      { text: 'Dashboard', link: '/student/home' }
    ]
  },
  {
    keywords: ['plan', 'plans', 'my plans', 'programs enrolled', 'enrolled programs', 'my programs'],
    question: 'How do I view my enrolled plans?',
    answer: `Your enrolled plans are easy to access! Here's how:

📋 **My Plans** - Go to "My Plans" to see all programs you're enrolled in
📊 **Progress Tracking** - See your progress for each program
📅 **Class Schedule** - View upcoming classes for each plan
💬 **Trainer Info** - Access your trainer's contact information
📈 **Completion Status** - Track how far you've come in each program

You can manage all your active plans from the "My Plans" section. Want to see your plans now?`,
    quickActions: [
      { text: 'My Plans', link: '/student/plans' },
      { text: 'Dashboard', link: '/student/home' }
    ]
  },
  {
    keywords: ['trainer', 'instructor', 'teacher', 'contact trainer', 'message trainer', 'my trainer'],
    question: 'How do I contact my trainer?',
    answer: `Connecting with your trainer is easy! Here's how:

👤 **My Instructor** - Go to "My Instructor" section in your dashboard
💬 **Message Trainer** - Click on your trainer's profile and use the "Message" button
📞 **Contact Info** - View your trainer's contact information
📅 **Schedule Sessions** - Request or schedule one-on-one sessions
💡 **Ask Questions** - Get help with your program or progress

Your trainer is there to support your journey! You can message them anytime through the platform. Want to contact your trainer now?`,
    quickActions: [
      { text: 'My Instructor', link: '/student/trainer' },
      { text: 'Dashboard', link: '/student/home' }
    ]
  },
  {
    keywords: ['marketplace', 'market', 'browse', 'shop', 'services', 'find program', 'discover'],
    question: 'How do I browse the marketplace?',
    answer: `The marketplace is where you discover amazing programs! Here's what you can do:

🛍️ **Browse Programs** - Explore all available programs by service type
🔍 **Search & Filter** - Find programs by trainer, price, or service category
👤 **View Trainer Profiles** - Learn about trainers before enrolling
💰 **Compare Pricing** - See different programs and their pricing
⭐ **Read Reviews** - Check out what other students are saying

You can access the marketplace from the main navigation. It's your one-stop shop for finding the perfect program! Want to explore now?`,
    quickActions: [
      { text: 'Browse Marketplace', link: '/student/market' },
      { text: 'View Services', link: '/student/services' }
    ]
  },
  {
    keywords: ['progress', 'tracking', 'achievement', 'stats', 'statistics', 'dashboard', 'performance'],
    question: 'How do I track my progress?',
    answer: `Tracking your progress helps you stay motivated! Here's what you can see:

📊 **Dashboard Stats** - View your overall progress on the main dashboard
📈 **Classes Attended** - See how many classes you've completed
🎯 **Progress Percentage** - Track completion for each program
🔥 **Streak Days** - See your consistency streak
🏆 **Achievements** - View badges and milestones you've earned
💪 **Calories Burned** - Track your fitness progress (if applicable)

Your dashboard gives you a complete overview of your wellness journey. Want to check your progress now?`,
    quickActions: [
      { text: 'View Dashboard', link: '/student/home' },
      { text: 'My Plans', link: '/student/plans' }
    ]
  },
  {
    keywords: ['profile', 'edit profile', 'update profile', 'settings', 'account', 'personal information'],
    question: 'How do I update my profile?',
    answer: `Keeping your profile updated is important! Here's how:

1. **Go to Profile** - Click on your profile icon or navigate to "My Profile"
2. **Edit Information** - Update your:
   • Name and contact details
   • Profile picture
   • Personal preferences
   • Health goals
3. **Save Changes** - Your updated profile helps trainers personalize your experience

You can also update your account settings from the "Settings" page. Want to update your profile now?`,
    quickActions: [
      { text: 'My Profile', link: '/student/profile' },
      { text: 'Settings', link: '/student/settings' }
    ]
  },
  {
    keywords: ['payment', 'payments', 'fee', 'fees', 'billing', 'invoice', 'pay'],
    question: 'How do I manage payments?',
    answer: `Managing payments is straightforward! Here's what you can do:

💳 **Payment History** - View all your past payments in your dashboard
📋 **Pending Payments** - See any payments that need to be completed
💰 **Payment Plans** - Check your current payment schedule
📧 **Invoices** - Access receipts and invoices for your records

You can see payment information on your dashboard and in your plan details. If you have questions about payments, you can contact your trainer or support. Need help with a specific payment?`,
    quickActions: [
      { text: 'View Dashboard', link: '/student/home' },
      { text: 'My Plans', link: '/student/plans' }
    ]
  },
  {
    keywords: ['help', 'support', 'contact', 'assistance', 'issue', 'problem', 'question'],
    question: 'How can I get help?',
    answer: `I'm here to help you on your wellness journey! You can get assistance in several ways:

💬 **Right here** - I can answer your questions about using the platform
📱 **WhatsApp Support** - Park your message and get a reply soon within official hours
📧 **Email Support** - Contact our support team for detailed assistance
💬 **Message Your Trainer** - Your trainer can help with program-specific questions
📚 **Dashboard Guide** - Explore your dashboard to discover all features

For technical issues, account problems, or general inquiries, our support team is ready to help. Is there something specific you need help with?`,
    quickActions: [
      { text: 'Contact Support', action: 'whatsapp' },
      { text: 'View Dashboard', link: '/student/home' }
    ]
  },
  {
    keywords: ['dashboard', 'home', 'overview', 'stats', 'summary'],
    question: 'What can I see on my dashboard?',
    answer: `Your Student Dashboard gives you a complete overview of your wellness journey! Here's what you'll see:

📊 **Key Metrics**:
   • Classes Attended
   • Programs Enrolled
   • Progress Achieved
   • Upcoming Classes
   • Pending Payments
   • Current Streak

🎯 **Learning Journey** - Track your progress through different milestones
📅 **Upcoming Classes** - See what's scheduled next
💪 **Activity Feed** - Recent activities and achievements
🔥 **Streak Counter** - Your consistency streak

The dashboard updates in real-time, so you always have the latest information about your progress. It's your command center for your wellness journey! Want to explore a specific section?`,
    quickActions: [
      { text: 'View Dashboard', link: '/student/home' },
      { text: 'My Plans', link: '/student/plans' }
    ]
  }
];

// Greeting message for student chatbot
const studentGreeting = "Hello! I'm Radhika, your Learning Assistant. I'm here to help you on your wellness journey with Yogvital. How can I assist you today? 🌿";

const StudentChatBot = () => {
  const { session } = useContext(UserContext);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const [messages, setMessages] = useState(() => {
    // Load messages from sessionStorage if available
    try {
      const savedMessages = sessionStorage.getItem('student-chatbot-messages');
      return savedMessages ? JSON.parse(savedMessages) : [];
    } catch {
      return [];
    }
  });
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const lastInteractionRef = useRef(null);

  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Start inactivity timer
  const startInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      const timeSinceLastInteraction = Date.now() - (lastInteractionRef.current || Date.now());
      if (timeSinceLastInteraction >= 60000 && messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.text !== "Is there anything else I can help you with? 😊") {
          addMessage('bot', "Is there anything else I can help you with? 😊", [
            'Contact Support',
            'No, thanks!'
          ]);
        }
      }
    }, 60000);
  };

  const resetInactivityTimer = () => {
    lastInteractionRef.current = Date.now();
    startInactivityTimer();
  };

  // Initialize with greeting
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addMessage('bot', studentGreeting);
      lastInteractionRef.current = Date.now();
      startInactivityTimer();
    } else if (isOpen && messages.length > 0) {
      resetInactivityTimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, messages.length]);

  // Auto-scroll to bottom
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const addMessage = (sender, text, quickActions = null) => {
    setMessages(prev => {
      const newMessages = [...prev, { sender, text, quickActions, timestamp: new Date() }];
      try {
        sessionStorage.setItem('student-chatbot-messages', JSON.stringify(newMessages));
      } catch {
        // Ignore storage errors
      }
      return newMessages;
    });
  };

  const findMatchingFAQ = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();

    for (const faq of studentFaqData) {
      for (const keyword of faq.keywords) {
        if (lowerMessage.includes(keyword.toLowerCase())) {
          return faq;
        }
      }
    }
    return null;
  };

  const handleQuickAction = (action) => {
    if (typeof action === 'string') {
      if (action === 'whatsapp') {
        const whatsappMessage = encodeURIComponent("Hi! I need more help with Yogvital as a student.");
        window.open(`https://wa.me/919876543210?text=${whatsappMessage}`, '_blank');
        addMessage('bot', "I've opened WhatsApp for you. Our support team will get back to you soon within official hours! 💚");
      } else if (action === 'cancel') {
        addMessage('bot', "No problem! Is there anything else I can help you with? 😊");
      } else if (action === 'enroll') {
        addMessage('user', 'How do I enroll in a program?');
        const faq = studentFaqData.find(f => f.keywords.includes('enroll'));
        if (faq) {
          setTimeout(() => {
            addMessage('bot', faq.answer, faq.quickActions);
          }, 500);
        }
      } else if (action === 'classes') {
        addMessage('user', 'How do I view my classes?');
        const faq = studentFaqData.find(f => f.keywords.includes('class'));
        if (faq) {
          setTimeout(() => {
            addMessage('bot', faq.answer, faq.quickActions);
          }, 500);
        }
      } else if (action === 'progress') {
        addMessage('user', 'How do I track my progress?');
        const faq = studentFaqData.find(f => f.keywords.includes('progress'));
        if (faq) {
          setTimeout(() => {
            addMessage('bot', faq.answer, faq.quickActions);
          }, 500);
        }
      } else if (action === 'retry') {
        addMessage('bot', "Sure! What would you like to know? I can help you with enrollment, classes, progress tracking, and more! 😊");
      } else {
        addMessage('bot', "I'm processing that for you...");
      }
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isTyping) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    addMessage('user', userMessage);
    resetInactivityTimer();

    // Simulate typing delay for more human-like interaction
    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);

      const lowerMessage = userMessage.toLowerCase().trim();

      // Check for greetings first
      const greetingPatterns = [
        'hi', 'hello', 'hey', 'hola', 'namaste', 'namaskar',
        'good morning', 'good afternoon', 'good evening', 'good night',
        'morning', 'afternoon', 'evening', 'night',
        'howdy', 'greetings', 'sup', 'what\'s up', 'whats up',
        'hi there', 'hello there', 'hey there'
      ];

      const isGreeting = greetingPatterns.some(pattern =>
        lowerMessage === pattern ||
        lowerMessage.startsWith(pattern + ' ') ||
        lowerMessage === pattern + '!'
      );

      if (isGreeting) {
        setTimeout(() => {
          addMessage('bot', `Hello! Great to hear from you! 👋 I'm here to help you on your wellness journey with Yogvital. What would you like to know today?`, [
            { text: 'How to enroll?', action: 'enroll' },
            { text: 'View my classes', action: 'classes' },
            { text: 'Track progress', action: 'progress' },
            { text: 'Browse marketplace', link: '/student/market' },
            { text: 'View dashboard', link: '/student/home' }
          ]);
        }, 500);
        return;
      }

      // Check for "how are you" or similar
      if (lowerMessage.includes('how are you') || lowerMessage.includes('how\'s it going') || lowerMessage.includes('how do you do')) {
        setTimeout(() => {
          addMessage('bot', `I'm doing great, thank you for asking! 😊 I'm here and ready to help you with your wellness journey. What can I assist you with today?`, [
            { text: 'Enroll in program', link: '/student/market' },
            { text: 'View my plans', link: '/student/plans' },
            { text: 'Check dashboard', link: '/student/home' }
          ]);
        }, 500);
        return;
      }

      // Check for "thanks" or "thank you"
      if (lowerMessage.includes('thank') || lowerMessage.includes('thanks')) {
        setTimeout(() => {
          addMessage('bot', `You're very welcome! 😊 I'm always here to help. Is there anything else you'd like to know about your wellness journey?`);
        }, 500);
        return;
      }

      // Find matching FAQ
      const matchedFAQ = findMatchingFAQ(userMessage);

      if (matchedFAQ) {
        // Add delay to simulate thinking
        setTimeout(() => {
          setIsTyping(false);
          addMessage('bot', matchedFAQ.answer, matchedFAQ.quickActions);
        }, 500);
      } else {
        // AI Response for "vigorous queries"
        (async () => {
          try {
            const response = await HttpInterceptor.post('/api/ai/general-chat', {
              message: userMessage,
              history: messages.slice(-6),
              type: 'student'
            });

            if (response.data?.success) {
              addMessage('bot', response.data.data.text);
            } else {
              throw new Error('AI response failed');
            }
          } catch (error) {
            console.error("AI Chat Error:", error);
            addMessage('bot', `Hmm, this query is a bit outside my current scope of knowledge. Let me connect you with our support team who can provide more detailed assistance! 😊`, [
              { text: 'Contact Support', action: 'whatsapp' },
              { text: 'Try Another Question', action: 'retry' }
            ]);
          } finally {
            setIsTyping(false);
          }
        })();
      }
    }, 800);
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Load messages from sessionStorage when opening
      try {
        const savedMessages = sessionStorage.getItem('student-chatbot-messages');
        if (savedMessages) {
          const parsed = JSON.parse(savedMessages);
          setMessages(parsed);
        }
      } catch {
        // Ignore errors
      }
    }
  };

  const refreshChat = () => {
    setMessages([]);
    sessionStorage.removeItem('student-chatbot-messages');
    if (isOpen) {
      addMessage('bot', studentGreeting);
      lastInteractionRef.current = Date.now();
      startInactivityTimer();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Only render for logged-in students/members
  // Don't render if no session (public users see ChatBot instead)
  if (!session) {
    return null;
  }

  // Show StudentChatBot for:
  // 1. Users who are not trainers (normal members/students)
  // 2. Trainers who are viewing the member dashboard (toggle to member view)
  const isOnTrainerDashboard = location.pathname.startsWith('/trainer');

  // If user is a trainer AND on trainer dashboard, hide StudentChatBot (they see TrainerChatBot)
  if (session.isTrainer && isOnTrainerDashboard) {
    return null;
  }

  // For trainers on student dashboard, show StudentChatBot
  // For non-trainers, always show StudentChatBot

  return (
    <>
      {/* Chat Button - Always visible when chat is closed */}
      <div className={`fixed ${isPWA ? 'bottom-4' : 'bottom-10'} right-4 sm:right-6 z-[9999] ${isOpen ? 'hidden' : 'block'}`}>
        <button
          onClick={toggleChat}
          className="bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 ring-4 ring-white/30 relative"
          aria-label="Open student assistant chat"
        >
          <FaComments className="text-2xl" />
          <span className="absolute top-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></span>
        </button>
      </div>

      {/* Close Button - Only visible when chat is open */}
      {isOpen && (
        <div className={`fixed ${isPWA ? 'bottom-4' : 'bottom-10'} right-4 sm:right-6 z-[9999]`}>
          <button
            onClick={toggleChat}
            className="bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 ring-4 ring-white/30"
            aria-label="Close student assistant chat"
          >
            <FaTimes className="text-xl" />
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`fixed ${isPWA ? 'bottom-20' : 'bottom-24'} right-4 sm:right-6 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl z-[9999] flex flex-col max-h-[600px] ring-4 ring-white/30`}
          style={{ position: 'fixed' }}>
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 text-white p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              {avatarError ? (
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold">
                  R
                </div>
              ) : (
                <img
                  src={radhikaAvatar}
                  alt="Radhika"
                  className="w-10 h-10 rounded-full border-2 border-white/50"
                  onError={() => setAvatarError(true)}
                />
              )}
              <div>
                <p className="font-semibold">Radhika</p>
                <p className="text-xs text-white/90">Learning Assistant • Ready to help</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={refreshChat}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Refresh chat"
                title="Refresh chat"
              >
                <FaSync className="text-sm" />
              </button>
              <button
                onClick={toggleChat}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Close chat"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
            style={{ maxHeight: '400px' }}
          >
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-start gap-2 max-w-[80%] ${msg.sender === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  {msg.sender === 'bot' && (
                    <div className="flex-shrink-0">
                      {avatarError ? (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-sm font-bold text-white">
                          R
                        </div>
                      ) : (
                        <img
                          src={radhikaAvatar}
                          alt="Radhika"
                          className="w-8 h-8 rounded-full border border-teal-200"
                          onError={() => setAvatarError(true)}
                        />
                      )}
                    </div>
                  )}
                  <div
                    className={`rounded-2xl px-4 py-2 ${msg.sender === 'user'
                        ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white'
                        : 'bg-white text-gray-800 shadow-sm border border-gray-200'
                      }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                    {msg.quickActions && (
                      <div className="mt-3 space-y-2">
                        {msg.quickActions.map((action, actionIdx) => {
                          if (typeof action === 'string') {
                            return (
                              <button
                                key={actionIdx}
                                onClick={() => handleQuickAction(action)}
                                className="block w-full text-left px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                              >
                                {action}
                              </button>
                            );
                          } else if (action.link) {
                            return (
                              <Link
                                key={actionIdx}
                                to={action.link}
                                onClick={() => {
                                  const element = document.getElementById(action.link.split('#')[1]);
                                  if (element) {
                                    element.scrollIntoView({ behavior: 'smooth' });
                                  }
                                }}
                                className="block w-full text-left px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                              >
                                {action.text}
                              </Link>
                            );
                          } else if (action.action) {
                            return (
                              <button
                                key={actionIdx}
                                onClick={() => handleQuickAction(action.action)}
                                className="block w-full text-left px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                              >
                                {action.text}
                              </button>
                            );
                          }
                          return null;
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0">
                  {avatarError ? (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-sm font-bold text-white">
                      R
                    </div>
                  ) : (
                    <img
                      src={radhikaAvatar}
                      alt="Radhika"
                      className="w-8 h-8 rounded-full border border-teal-200"
                      onError={() => setAvatarError(true)}
                    />
                  )}
                </div>
                <div className="bg-white rounded-2xl px-4 py-2 shadow-sm border border-gray-200">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-2xl">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isTyping}
                className="p-2 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg hover:from-teal-700 hover:to-cyan-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Send message"
              >
                <FaPaperPlane />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StudentChatBot;

